import type { gsap } from 'gsap';

export const at = (label: string, offset = 0) =>
  offset ? `${label}+=${offset}` : label;

/**
 * Everything in this diagram accumulates: once a line or outline is drawn it
 * stays lit until the whole diagram fades out at the end of the loop (or at a
 * "no" decision). That's the main difference from the painpoint animation,
 * whose pulses collapse behind themselves — so plain DrawSVG ranges are all
 * that's needed here.
 */

/**
 * Draw a closed shape (card outline or the logo ring) outward from `entry`,
 * a % along its path, growing equally in both directions until each side has
 * covered `to` % of the perimeter. `to: 25` lights half the shape, ending at
 * the two points a quarter-turn away; `to: 50` closes it on the far side.
 * Pass `from` to continue a partial draw. DrawSVG's dash period equals the
 * path length, so ranges below 0% or above 100% wrap round.
 */
export function growFrom(
  tl: gsap.core.Timeline,
  id: string,
  position: string,
  {
    entry,
    from = 0,
    to,
    duration,
  }: { entry: number; from?: number; to: number; duration: number },
) {
  tl.fromTo(
    `#${id}`,
    { drawSVG: `${entry - from}% ${entry + from}%` },
    { drawSVG: `${entry - to}% ${entry + to}%`, duration, ease: 'none' },
    position,
  );
  return duration;
}

/** Draw a straight connector from start to end. */
/** Seconds a `pulse()` occupies on the timeline. */
export const PULSE_DURATION = 0.8;

/**
 * Two gentle heartbeats (scale up and back) while a decision is being made.
 * Ends back at scale 1 so nothing needs resetting on the next loop.
 */
export function pulse(
  tl: gsap.core.Timeline,
  target: gsap.TweenTarget,
  position: gsap.Position,
) {
  // fromTo so a rebuild mid-pulse can't leave the card stuck scaled.
  tl.fromTo(
    target,
    { scale: 1 },
    {
      scale: 1.05,
      duration: PULSE_DURATION / 4,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: 3,
    },
    position,
  );
  return PULSE_DURATION;
}

export function drawLine(
  tl: gsap.core.Timeline,
  id: string,
  position: string,
  duration: number,
) {
  tl.fromTo(
    `#${id}`,
    { drawSVG: '0% 0%' },
    { drawSVG: '0% 100%', duration, ease: 'none' },
    position,
  );
  return duration;
}
